import type { LoaderFunctionArgs } from 'react-router-dom'
import { apolloClient } from '@lib/apollo/client'
import { GET_APPOINTMENT } from '@graphql/meeting/queries.gql'
import type { GetAppointmentQuery, GetAppointmentQueryVariables } from '@models/generated/graphql'
import { redirectToAccountWithRedirection } from '@/utils/redirection.utils'

export type PreJoinLoaderData = {
  appointment: NonNullable<GetAppointmentQuery['appointment']>
}

/**
 * Loader pour la page de pré-jointure
 * Charge les données du rendez-vous avant le rendu du composant
 */
export async function prejoinLoader({ params }: LoaderFunctionArgs): Promise<PreJoinLoaderData> {
  const { id } = params

  // Validation de l'ID
  if (!id || !id.trim()) {
    throw new Response('Identifiant de rendez-vous manquant', {
      status: 400,
      statusText: 'Bad Request'
    })
  }

  try {
    const { data, error, errors } = await apolloClient.query<GetAppointmentQuery, GetAppointmentQueryVariables>({
      query: GET_APPOINTMENT,
      variables: { id: id.trim() }
    })

    if (errors && errors.length > 0) {
      if (errors.some((e) => e.extensions?.code === 'IMMERSION_APPOINTMENT_UNAUTHORIZED')) {
        redirectToAccountWithRedirection('/')
      }
    }

    // Gestion des erreurs GraphQL
    if (error) {
      throw new Response(error.message, {
        status: 500,
        statusText: 'GraphQL Error'
      })
    }

    // Vérification que le rendez-vous existe
    if (!data.appointment) {
      throw new Response("Ce rendez-vous n'existe pas ou n'est plus disponible.", {
        status: 404,
        statusText: 'Rendez-vous introuvable'
      })
    }

    return {
      appointment: data.appointment
    }
  } catch (error) {
    // Si c'est déjà une Response, on la relance
    if (error instanceof Response) {
      throw error
    }

    // Erreur réseau ou autre erreur inattendue
    throw new Response('Impossible de charger le rendez-vous. Veuillez réessayer.', {
      status: 500,
      statusText: 'Internal Server Error'
    })
  }
}
